import clsx from 'clsx'
import { LinearGradient } from 'expo-linear-gradient'
import type { ReactElement, ReactNode } from 'react'
import { Platform, View } from 'react-native'

import { Image } from '@/src/elements/image'
import { ResponsiveDimensions } from '@/src/general/components/ResponsiveDimensions'
import { ResponsiveDisplay } from '@/src/general/components/ResponsiveDisplay'
import { TEST_ID } from '@/src/general/constants/testId'
import { isNative } from '@/src/general/utils/isNative'
import { isWeb } from '@/src/general/utils/isWeb'

import { Spacer } from '../spacer/Spacer'
import { CommonHeader } from '../templates/CommonLayout'

const imageContainerClassName = 'max-h-[429px] min-h-96 w-full px-5 md:max-w-[800px] rounded-3xl'

type TAnimatedParallaxListHeaderProps = {
  caption?: ReactElement
  content: ReactElement
  imageUri: string
  CustomHeader?: ReactElement
  ImageComponent?: () => ReactNode
}

export const AnimatedParallaxListHeader = ({
  caption,
  content,
  imageUri,
  CustomHeader,
  ImageComponent,
}: TAnimatedParallaxListHeaderProps): ReactNode => {
  return (
    <View className="max-w-screen min-w-screen min-w-full flex-col items-center">
      <ResponsiveDisplay behavior="maxWidth" size="xs">
        <View testID={TEST_ID.MISCELLANEOUS.PARALLAX_FLAT_LIST_SMALL_HEADER} className="absolute z-zIndex2 w-full">
          {!CustomHeader && <CommonHeader contrast />}
          {CustomHeader}
        </View>
      </ResponsiveDisplay>
      <ResponsiveDisplay size="xs" behavior="maxWidth">
        {isWeb() && <View className="w-full" style={{ height: 280 }} />}
        {isNative() && <View style={{ height: 200 }} />}
      </ResponsiveDisplay>
      <ResponsiveDisplay size="sm" behavior="minWidth">
        {!ImageComponent && imageUri && (
          <View className={imageContainerClassName} testID={TEST_ID.MISCELLANEOUS.PARALLAX_FLAT_LIST_IMAGE_DESKTOP}>
            <Image radius="3xl" contentFit="cover" source={{ uri: imageUri }} />
          </View>
        )}
        {ImageComponent && <ImageComponent />}
      </ResponsiveDisplay>

      <ResponsiveDimensions>
        {({ xs }) => (
          <>
            <View
              className={clsx('w-full gap-7 px-5 pb-2 md:max-w-screen-md', {
                'bg-white': xs,
                'bg-transparent': !xs,
              })}
            >
              {xs && (
                <View className="min-w-screen absolute left-0 right-0 top-0 mt-[-10px] min-h-44 rounded-t-lg bg-white">
                  {Platform.OS === 'web' && (
                    <LinearGradient
                      className="absolute -bottom-14 left-0 right-0 h-14"
                      colors={['white', 'transparent']}
                    />
                  )}
                </View>
              )}
              {content}
            </View>

            {caption && (
              <View className={clsx('w-full items-center bg-transparent', { 'bg-white': xs })}>
                <Spacer y={2} />
                <View className="mt-8 w-full gap-4 px-5 md:max-w-screen-md">{caption}</View>
              </View>
            )}
          </>
        )}
      </ResponsiveDimensions>
    </View>
  )
}
